import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Phone, MapPin, ArrowRight, ChevronLeft, CheckCircle2,
  ShieldCheck, Timer, BadgeCheck,
} from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import QuoteForm from "@/components/landing/QuoteForm";
import Testimonials from "@/components/landing/Testimonials";
import {
  CITIES, CITY_LIST, COUNTY_CONTEXT,
  getCityContent, getLocalizedFaqs,
} from "@/data/cities";
import { CORE_SERVICES, CORE_SERVICE_MAP } from "@/data/services";
import { COMPANY } from "@/data/site";
import {
  setSeo,
  buildLocalBusinessSchema,
  buildBreadcrumbSchema,
  buildFaqSchema,
} from "@/lib/seo";

function NotFound() {
  return (
    <main className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-4xl font-black text-[#1E3A5F]">City not found</h1>
        <p className="text-slate-600 mt-3">The service area page you're looking for doesn't exist.</p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/" className="btn-outline-dark inline-flex items-center px-5 py-2.5">
            <ChevronLeft size={16} /> Back home
          </Link>
          <Link to="/service-areas" className="btn-cta inline-flex items-center px-5 py-2.5">
            See all service areas
          </Link>
        </div>
      </div>
      <Footer />
    </main>
  );
}

export default function CityLandingPage() {
  const { citySlug } = useParams();
  const city = CITIES[citySlug];

  useEffect(() => {
    if (city) {
      const title = `Plumber in ${city.name}, MI · Peak Plumbing`;
      const description = `Peak Plumbing serves ${city.name}, MI with drain, sewer, water heater, leak detection, sump pump and fixture repair. ${city.county}-focused, family-owned, upfront pricing. Call (586) 351-6271.`;
      setSeo({
        title,
        description,
        path: `/service-areas/${city.slug}`,
        jsonLd: [
          {
            id: "org",
            data: buildLocalBusinessSchema({ areaServed: [`${city.name}, MI`, city.county] }),
          },
          {
            id: "breadcrumbs",
            data: buildBreadcrumbSchema([
              { name: "Home", path: "/" },
              { name: "Service Areas", path: "/service-areas" },
              { name: city.name, path: `/service-areas/${city.slug}` },
            ]),
          },
          { id: "faq", data: buildFaqSchema(getLocalizedFaqs(city)) },
        ],
      });
    }
    window.scrollTo(0, 0);
  }, [citySlug, city]);

  if (!city) return <NotFound />;

  const content = getCityContent(city);
  const county = COUNTY_CONTEXT[city.county];
  const localizedFaqs = getLocalizedFaqs(city);
  const priorityServices = content.servicePriority
    .map((slug) => CORE_SERVICE_MAP[slug])
    .filter(Boolean);
  const otherServices = CORE_SERVICES.filter(
    (s) => !content.servicePriority.includes(s.slug)
  );
  const nearbyCities = CITY_LIST
    .filter((c) => c.slug !== city.slug && c.county === city.county)
    .slice(0, 8);

  return (
    <main data-testid={`city-page-${city.slug}`} className="bg-white">
      <Navbar />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-6 text-sm text-slate-500">
        <Link to="/" className="hover:text-[#A88852]">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/service-areas" className="hover:text-[#A88852]">Service Areas</Link>
        <span className="mx-2">/</span>
        <span className="text-[#1E3A5F] font-semibold">{city.name}</span>
      </nav>

      {/* Hero */}
      <section className="relative bg-white">
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 pt-10 pb-16 md:pt-14 md:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 rounded-full">
              <MapPin size={12} className="text-[#C5A572]" />
              <span className="text-xs font-semibold tracking-wide text-slate-700">
                Serving {city.name}, MI · {city.county}
              </span>
            </div>

            <h1 className="font-display text-4xl md:text-6xl font-black tracking-tight text-[#1E3A5F] mt-5 leading-[1.02]">
              Plumber in <span className="text-[#A88852]">{city.name}</span>, MI.
            </h1>

            <p className="text-slate-700 text-lg mt-5 max-w-xl leading-relaxed font-semibold">
              {content.tagline}
            </p>

            <p className="text-slate-600 text-base md:text-lg mt-4 max-w-2xl leading-relaxed">
              {content.intro}
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-7">
              <a
                data-testid={`city-${city.slug}-call`}
                href={`tel:${COMPANY.phoneTel}`}
                className="btn-cta inline-flex items-center gap-2.5 px-6 py-3.5 text-base"
              >
                <Phone size={18} /> Call {COMPANY.phone}
              </a>
              <a href="#quote" className="btn-outline-dark inline-flex items-center px-6 py-3.5 text-base">
                Schedule Appointment in {city.name}
              </a>
            </div>
          </div>

          <div id="quote" className="lg:col-span-5">
            <QuoteForm source={`city-${city.slug}`} />
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-[#122744] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-14 grid md:grid-cols-3 gap-8">
          {[
            { icon: Timer, label: "Fast dispatch", sub: `Same-day availability across ${city.name}` },
            { icon: ShieldCheck, label: "Fully insured", sub: "Peace of mind on every job" },
            { icon: BadgeCheck, label: "1-year warranty", sub: "On every job we do" },
          ].map((s, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="w-11 h-11 grid place-items-center bg-[#1E3A5F] rounded-sm shrink-0">
                <s.icon size={22} className="text-[#D9BF8C]" />
              </div>
              <div>
                <div className="font-display font-black text-2xl">{s.label}</div>
                <div className="text-slate-300 text-sm mt-0.5">{s.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* County-specific context (unique per city via county) */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="label-eyebrow">{city.county} context</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E3A5F] mt-2 tracking-tight">
              What we see most in {city.name}.
            </h2>
            <p className="text-slate-600 md:text-lg mt-5 max-w-md leading-relaxed">
              {content.countyHousing}
            </p>
            <p className="text-slate-600 md:text-lg mt-4 max-w-md leading-relaxed">
              {content.countyWeather}
            </p>
          </div>

          <div className="lg:col-span-7">
            <h3 className="font-display text-xl font-bold text-[#1E3A5F]">
              Most common plumbing issues in {city.county}
            </h3>
            <ul className="mt-4 space-y-3">
              {content.challenges.map((c, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 size={18} className="text-[#A88852] mt-1 shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Priority services for this county (Hub-and-spoke internal linking) */}
      <section className="bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="label-eyebrow">Most-requested in {city.name}</div>
          <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E3A5F] mt-2 tracking-tight max-w-2xl">
            Peak Plumbing services for {city.name} homes.
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl">
            These are the services {city.county} homeowners call us for most often — click any for local pricing and scheduling details.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {priorityServices.map((svc) => (
              <Link
                key={svc.slug}
                to={`/service-areas/${city.slug}/${svc.slug}`}
                data-testid={`city-priority-${svc.slug}`}
                className="group bg-white border border-slate-200 rounded-md p-5 hover:border-[#C5A572] transition"
              >
                <div className="font-display font-bold text-[#1E3A5F]">{svc.title}</div>
                <div className="text-xs text-slate-500 mt-1">in {city.name}, MI</div>
                <div className="mt-3 flex items-center gap-1 text-orange-600 font-semibold text-sm group-hover:gap-2 transition-all">
                  View service <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10">
            <h3 className="font-display text-lg font-bold text-[#1E3A5F] mb-4">
              All core plumbing services in {city.name}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {otherServices.map((svc) => (
                <Link
                  key={svc.slug}
                  to={`/service-areas/${city.slug}/${svc.slug}`}
                  data-testid={`city-service-link-${svc.slug}`}
                  className="group text-sm bg-white border border-slate-200 rounded-md px-4 py-3 flex items-center justify-between hover:border-[#C5A572] transition"
                >
                  <span className="font-semibold text-[#1E3A5F]">{svc.title}</span>
                  <ArrowRight size={12} className="text-[#A88852] group-hover:translate-x-1 transition" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Localized FAQ */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="label-eyebrow">{city.name} FAQ</div>
          <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E3A5F] mt-2 tracking-tight">
            Common questions from {city.name} homeowners.
          </h2>
          <dl className="mt-8 space-y-4">
            {localizedFaqs.map((f, i) => (
              <div
                key={i}
                data-testid={`city-faq-${i}`}
                className="bg-slate-50 border border-slate-200 rounded-md p-5"
              >
                <dt className="font-display font-bold text-[#1E3A5F]">{f.q}</dt>
                <dd className="text-slate-700 mt-2 text-sm leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <Testimonials />

      {/* Nearby cities (same county) */}
      <section className="bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="label-eyebrow">Nearby {city.county} service areas</div>
          <h2 className="font-display text-3xl md:text-4xl font-black text-[#1E3A5F] mt-2 tracking-tight">
            Also serving {city.county}.
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            {nearbyCities.map((c) => (
              <Link
                key={c.slug}
                to={`/service-areas/${c.slug}`}
                data-testid={`city-nearby-${c.slug}`}
                className="group bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between hover:border-[#C5A572] transition"
              >
                <div>
                  <div className="font-display font-bold text-[#1E3A5F]">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.county}</div>
                </div>
                <ArrowRight size={16} className="text-[#A88852] group-hover:translate-x-1 transition" />
              </Link>
            ))}
          </div>
          <div className="mt-6">
            <Link to="/service-areas" className="text-sm font-semibold text-[#A88852] hover:underline">
              See all service areas →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
